<template>
  <div class="label-page">
    <div class="page-header">
      <h2 class="page-title">碳标签</h2>
    </div>

    <filter-bar>
      <div class="filter-item">
        <span class="filter-label">产品名称</span>
        <el-input
          v-model="productName"
          size="small"
          placeholder="请输入产品名称"
          clearable
          style="width: 160px"
        />
      </div>
      <div class="filter-item">
        <span class="filter-label">标签状态</span>
        <el-select v-model="status" size="small" clearable placeholder="全部" style="width: 120px">
          <el-option
            v-for="item in statusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </div>
      <div class="filter-item">
        <span class="filter-label">申请年度</span>
        <el-date-picker
          v-model="year"
          type="year"
          size="small"
          placeholder="全部"
          clearable
          value-format="yyyy"
          style="width: 120px"
        />
      </div>
      <el-button type="primary" size="small" @click="loadData">查询</el-button>
    </filter-bar>

    <div class="stat-cards-row stat-cards-3">
      <stat-card
        title="标签总数"
        :today="summary.total.today"
        :annual="summary.total.annual"
        :unit="summary.total.unit"
        :annual-unit="summary.total.unit"
        icon="el-icon-price-tag"
        color="#409EFF"
      />
      <stat-card
        title="已发布"
        :today="summary.published.today"
        :annual="summary.published.annual"
        :unit="summary.published.unit"
        :annual-unit="summary.published.unit"
        icon="el-icon-circle-check"
        color="#67C23A"
      />
      <stat-card
        title="待审核"
        :today="summary.draft.today"
        :annual="summary.draft.annual"
        :unit="summary.draft.unit"
        :annual-unit="summary.draft.unit"
        icon="el-icon-time"
        color="#E6A23C"
      />
    </div>

    <div class="table-section">
      <div class="table-toolbar">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openApplyDialog">
          申请标签
        </el-button>
      </div>
      <el-table :data="labels" border stripe size="small">
        <el-table-column prop="labelNo" label="标签编号" width="130" />
        <el-table-column prop="productName" label="产品名称" min-width="160" show-overflow-tooltip />
        <el-table-column prop="footprint" label="碳足迹 (kgCO2e)" width="140" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.footprint, 2) }}</template>
        </el-table-column>
        <el-table-column prop="functionalUnit" label="功能单位" width="100" />
        <el-table-column prop="standard" label="核算标准" width="200" show-overflow-tooltip />
        <el-table-column prop="validFrom" label="有效期起" width="110">
          <template slot-scope="{ row }">{{ row.validFrom || '-' }}</template>
        </el-table-column>
        <el-table-column prop="validTo" label="有效期止" width="110">
          <template slot-scope="{ row }">{{ row.validTo || '-' }}</template>
        </el-table-column>
        <el-table-column prop="statusLabel" label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 'published' ? 'success' : 'warning'" size="mini">
              {{ row.statusLabel }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openPreview(row)">预览</el-button>
            <el-button
              v-if="row.status === 'published'"
              type="text"
              size="small"
              @click="handleExport(row)"
            >下载</el-button>
            <el-button
              v-if="row.status === 'draft'"
              type="text"
              size="small"
              @click="handlePublish(row)"
            >发布</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog
      title="申请碳标签"
      :visible.sync="applyDialogVisible"
      width="520px"
      @close="resetApplyForm"
    >
      <el-form ref="applyForm" :model="applyForm" :rules="applyRules" label-width="100px" size="small">
        <el-form-item label="关联任务" prop="evaluationId">
          <el-select v-model="applyForm.evaluationId" placeholder="请选择评价通过的任务" style="width: 100%">
            <el-option
              v-for="item in availableTasks"
              :key="item.id"
              :label="item.productName"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="有效期" prop="validRange">
          <el-date-picker
            v-model="applyForm.validRange"
            type="daterange"
            size="small"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            style="width: 100%"
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="applyDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="handleApply">提交申请</el-button>
      </div>
    </el-dialog>

    <el-drawer
      :title="currentLabel ? '碳标签预览' : ''"
      :visible.sync="previewVisible"
      size="400px"
      direction="rtl"
    >
      <div v-if="currentLabel" class="label-preview">
        <div class="label-card">
          <div class="label-header">
            <i class="el-icon-price-tag"></i>
            <span>产品碳足迹标签</span>
          </div>
          <div class="label-body">
            <div class="product-name">{{ currentLabel.productName }}</div>
            <div class="footprint-value">
              {{ formatNumber(currentLabel.footprint, 2) }}
              <span class="unit">kgCO2e</span>
            </div>
            <div class="functional-unit">功能单位：{{ currentLabel.functionalUnit }}</div>
            <div class="standard">核算标准：{{ currentLabel.standard }}</div>
            <div class="validity">
              有效期：{{ currentLabel.validFrom || '-' }} 至 {{ currentLabel.validTo || '-' }}
            </div>
          </div>
          <div class="label-qr">
            <div class="qr-placeholder">
              <i class="el-icon-picture-outline"></i>
              <span>二维码</span>
            </div>
            <div class="label-no">{{ currentLabel.labelNo }}</div>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import StatCard from '@/components/common/StatCard.vue'
import { getLabelData } from '@/api/modules/carbonFootprint'
import { formatNumber } from '@/utils/format'

export default {
  name: 'CarbonFootprintLabel',
  components: { FilterBar, StatCard },
  data() {
    return {
      productName: '',
      status: '',
      year: '',
      statusOptions: [],
      summary: {
        total: { today: 0, annual: 0, unit: '个' },
        published: { today: 0, annual: 0, unit: '个' },
        draft: { today: 0, annual: 0, unit: '个' }
      },
      labels: [],
      allLabels: [],
      availableTasks: [],
      applyDialogVisible: false,
      applyForm: {
        evaluationId: '',
        validRange: []
      },
      applyRules: {
        evaluationId: [{ required: true, message: '请选择关联任务', trigger: 'change' }],
        validRange: [{ required: true, message: '请选择有效期', trigger: 'change' }]
      },
      previewVisible: false,
      currentLabel: null,
      nextLabelNo: 4
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatNumber,
    async loadData() {
      const res = await getLabelData({
        productName: this.productName,
        status: this.status,
        year: this.year
      })
      const data = res.data
      this.statusOptions = data.statusOptions
      this.availableTasks = data.availableTasks
      if (!this.allLabels.length) {
        this.allLabels = data.labels.map(item => ({ ...item }))
      }
      this.applyFilters()
    },
    applyFilters() {
      let list = [...this.allLabels]
      if (this.productName) {
        const kw = this.productName.trim()
        list = list.filter(item => item.productName.includes(kw))
      }
      if (this.status) {
        list = list.filter(item => item.status === this.status)
      }
      if (this.year) {
        list = list.filter(item => String(item.year) === String(this.year))
      }
      this.labels = list
      this.updateSummary(list)
    },
    updateSummary(list) {
      const published = list.filter(item => item.status === 'published').length
      const draft = list.filter(item => item.status === 'draft').length
      this.summary = {
        total: { today: list.length, annual: list.length, unit: '个' },
        published: { today: published, annual: published, unit: '个' },
        draft: { today: draft, annual: draft, unit: '个' }
      }
    },
    openApplyDialog() {
      if (!this.availableTasks.length) {
        this.$message.warning('暂无可申请标签的评价通过任务')
        return
      }
      this.applyDialogVisible = true
    },
    resetApplyForm() {
      this.applyForm = { evaluationId: '', validRange: [] }
      if (this.$refs.applyForm) {
        this.$refs.applyForm.resetFields()
      }
    },
    handleApply() {
      this.$refs.applyForm.validate(valid => {
        if (!valid) return
        const task = this.availableTasks.find(t => t.id === this.applyForm.evaluationId)
        if (!task) return
        const labelNo = `CL-2025-${String(this.nextLabelNo++).padStart(3, '0')}`
        this.allLabels.unshift({
          id: `label-${Date.now()}`,
          labelNo,
          productName: task.productName,
          accountingId: task.accountingId,
          evaluationId: task.id,
          footprint: task.footprint,
          functionalUnit: task.functionalUnit,
          standard: task.standard,
          validFrom: this.applyForm.validRange[0],
          validTo: this.applyForm.validRange[1],
          year: 2025,
          status: 'draft',
          statusLabel: '待审核'
        })
        this.applyDialogVisible = false
        this.applyFilters()
        this.$message.success('标签申请已提交')
      })
    },
    openPreview(row) {
      this.currentLabel = row
      this.previewVisible = true
    },
    handleExport(row) {
      this.$message.success(`正在下载标签「${row.labelNo}」`)
    },
    handlePublish(row) {
      row.status = 'published'
      row.statusLabel = '已发布'
      if (!row.validFrom) {
        row.validFrom = new Date().toISOString().slice(0, 10)
        const end = new Date()
        end.setFullYear(end.getFullYear() + 1)
        row.validTo = end.toISOString().slice(0, 10)
      }
      this.applyFilters()
      this.$message.success('标签已发布')
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.page-header {
  margin-bottom: 16px;

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: @text-primary;
    margin: 0;
  }
}

.stat-cards-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.table-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
}

.table-toolbar {
  margin-bottom: 12px;
}

.label-preview {
  padding: 16px;
}

.label-card {
  border: 2px solid @primary-color;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

.label-header {
  background: @primary-color;
  color: #fff;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.label-body {
  padding: 20px 16px;

  .product-name {
    font-size: 16px;
    font-weight: 600;
    color: @text-primary;
    margin-bottom: 12px;
  }

  .footprint-value {
    font-size: 32px;
    font-weight: 700;
    color: @active-color;
    margin-bottom: 8px;

    .unit {
      font-size: 14px;
      font-weight: 400;
      color: @text-secondary;
    }
  }

  .functional-unit,
  .standard,
  .validity {
    font-size: 13px;
    color: @text-secondary;
    margin-bottom: 4px;
  }
}

.label-qr {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  border-top: 1px dashed #e4e7ed;

  .qr-placeholder {
    width: 100px;
    height: 100px;
    border: 1px dashed #dcdfe6;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #c0c4cc;
    font-size: 12px;
    margin-bottom: 8px;

    i {
      font-size: 32px;
      margin-bottom: 4px;
    }
  }

  .label-no {
    font-size: 12px;
    color: @text-secondary;
  }
}
</style>
